import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  DocumentCheckIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  BoltIcon,
  MagnifyingGlassIcon,
  MegaphoneIcon,
  ArrowsPointingInIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

/* ─── Animation Helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ duration: 0.7, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, scale: 0.95 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.6, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */
const capabilities = [
  {
    icon: MegaphoneIcon,
    title: 'Marketing Compliance Scanner',
    description:
      'Upload any ad, email, push notification, or landing page copy for instant AI-powered compliance scanning. Checks against UDAAP (unfair, deceptive, abusive acts or practices), Reg Z trigger terms, Reg DD deposit disclosures, FDIC membership requirements, Equal Housing, CAN-SPAM, and state-specific rules. Every finding cites the specific regulation, the detected text, and the required corrective action.',
  },
  {
    icon: DevicePhoneMobileIcon,
    title: 'Social Media Compliance',
    description:
      'Dedicated compliance checker for social media posts. Select the platform (Instagram, Facebook, X, LinkedIn, TikTok, YouTube), paste the post text, and get instant analysis against UDAAP, Reg Z, FDIC, Equal Housing, and CAN-SPAM regulations. Platform-specific character limits shown as you type. Enforcement cross-reference surfaces real regulatory actions against similar language.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Website Compliance Scanner',
    description:
      'Automated crawler that scans your website for disclosure compliance and UDAAP issues. Configurable crawl depth and page limits. Checks every page for FDIC membership disclosures, rate advertising compliance, trigger terms, and potentially deceptive language. Your website IS your branch — it should be as compliant as a physical lobby.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'UDAAP Risk Scoring',
    description:
      'Three-dimensional UDAAP risk scoring on every piece of content: Unfair (0-100), Deceptive (0-100), Abusive (0-100). Not just "pass/fail" — quantified risk that lets your team prioritize what to fix first. Every score is backed by specific regulatory citations and plain-language explanations.',
  },
  {
    icon: MagnifyingGlassIcon,
    title: 'Regulatory Intelligence',
    description:
      'Automated monitoring of enforcement actions and regulatory changes from CFPB, OCC, and FDIC. Know when new guidance or enforcement patterns affect digital banking, marketing claims, or consumer protection. "Am I Exposed?" analysis cross-references your compliance program against live enforcement trends.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'Compliance Program Management',
    description:
      'Policy management with version control, compliance calendar with automated deadlines, complaint tracking and resolution workflows, and a regulatory knowledge base. Board-ready reports that aggregate findings across all modules — marketing compliance stats, regulatory alerts, complaint trends, and examination readiness.',
  },
];

const varoSpecificBenefits = [
  {
    icon: BoltIcon,
    title: 'Digital Scale, Examiner-Grade Rigor',
    description:
      'A digital bank produces 10-100x the marketing content of a traditional bank — push notifications, in-app promotions, email campaigns, social posts, paid ads, landing pages. Manual UDAAP review at that volume means either slowing down marketing or accepting risk. Clarid scans in seconds so the compliance team keeps pace without becoming a bottleneck.',
  },
  {
    icon: ExclamationTriangleIcon,
    title: 'No Sponsor Bank Buffer',
    description:
      'Most neobanks rely on sponsor banks that share compliance responsibility. Varo chose a full charter — which means OCC, FDIC, and CFPB examine Varo directly. Every marketing claim, every customer communication, every product disclosure is Varo\'s responsibility alone. Clarid provides the continuous monitoring that matches the scope of that responsibility.',
  },
  {
    icon: ChartBarIcon,
    title: 'UDAAP Enforcement Is Accelerating',
    description:
      'CFPB has levied $5B+ in UDAAP penalties. Digital banks are prime targets — "no hidden fees," "instant cash," and "build your credit" are all claims that draw scrutiny. Clarid flags these patterns before they become findings, with specific regulatory citations that explain why each claim is risky and what to change.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Examiner-Ready Audit Trail',
    description:
      'Every content scan creates an immutable compliance record: the content analyzed, the AI detection results, the rule engine findings, the compliance officer\'s approval decision, and the SHA-256 data hash — all timestamped. When the OCC asks "Show me your marketing compliance program," you have the answer in seconds.',
  },
];

const interconnectivityFlows = [
  {
    title: 'Marketing Findings Feed Board Reports',
    description: 'Compliance scan results, UDAAP risk scores, and approval rates automatically roll up into quarterly board reports — no manual assembly.',
  },
  {
    title: 'Enforcement Actions Flag Your Content',
    description: 'When the CFPB takes action against another bank for similar marketing language, Clarid surfaces the connection — "This language pattern was cited in CFPB enforcement action #2026-XX."',
  },
  {
    title: 'Website + Social + Email in One View',
    description: 'Content library shows every piece of marketing across every channel with its compliance status. Nothing goes out unchecked, and the examiner can see the full picture.',
  },
  {
    title: 'Complaint Patterns Connect to Marketing Risk',
    description: 'Complaint categories (misleading advertising, fee disputes, product misrepresentation) correlate with marketing compliance findings — creating a closed-loop risk signal.',
  },
];

const dueDiligenceDocs = [
  'AI Governance Policy',
  'FS AI RMF Gap Analysis (230 control objectives, 100% coverage)',
  'Model Inventory with validation framework',
  'Bias Testing Methodology & Results',
  'Data Flow Architecture Documentation',
  'Information Security Program (WISP)',
  'Incident Response Plan',
  'Business Continuity Plan',
];

const nextSteps = [
  { num: 1, title: 'Live Demo', description: 'Pull a current Varo ad and run it through Clarid\'s UDAAP scanner live — see exactly what the system flags, how it scores risk, and what it recommends' },
  { num: 2, title: 'Requirements Alignment', description: 'Map Clarid\'s capabilities to Varo\'s specific compliance program needs — marketing review workflow, regulatory monitoring priorities, board reporting cadence' },
  { num: 3, title: 'Pilot', description: 'Structured pilot with defined success criteria — volume of content reviewed, time savings measured, examiner documentation generated' },
  { num: 4, title: 'Production Deployment', description: 'Full deployment with team onboarding, workflow integration, and ongoing support' },
];

/* ─── Component ─── */
export default function VaroPartnerPage() {
  useEffect(() => {
    document.title = 'Varo Bank + Clarid — Charter-Grade Compliance for Digital Banking';

    const metaTags: Record<string, string> = {
      'og:title': 'Varo Bank + Clarid — Charter-Grade Compliance for Digital Banking',
      'og:description': 'AI-native compliance platform for chartered digital banks. Marketing compliance, UDAAP monitoring, and examiner-ready documentation at digital scale.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
    };

    const existingTags: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      existingTags.push(meta);
    }

    return () => {
      existingTags.forEach((tag) => document.head.removeChild(tag));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* ─── Minimal Header ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-5">
            <img src="/varo-logo.svg" alt="Varo Bank" className="h-8" />
            <span className="text-slate-300 text-xl font-light">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
          </div>
        </div>
      </header>

      {/* ─── Section 1: Hero ─── */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-[#8C58D0] mb-8">
              Charter-Grade Compliance for Digital Banking
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">
              You Own the Charter. Own the Compliance.
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl lg:text-[1.65rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Varo made the boldest move in fintech — earning a full national bank charter and taking
              direct responsibility for every compliance obligation. Clarid is the AI-native platform
              that gives your compliance program the same operational advantage your technology gives
              your banking products.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 2: The Digital Bank Compliance Challenge ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                The Chartered Fintech Compliance Reality
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Most neobanks outsource compliance risk to sponsor banks. Varo chose the opposite path — a
                  full OCC charter that puts every regulatory obligation squarely on your team. No buffer. No
                  shared responsibility. When the OCC and CFPB examine Varo, they examine <em>Varo</em>.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  That charter is a competitive advantage — but it means the compliance program has to match
                  the scale of a digital bank that acquires customers entirely through marketing. Every push
                  notification is a potential UDAAP claim. Every landing page is an advertisement. Every
                  in-app promotion is a disclosure obligation. At digital scale, manual compliance review
                  becomes the bottleneck between "campaign approved" and "campaign launched."
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  And the CFPB is watching. UDAAP enforcement has produced over $5 billion in penalties,
                  with digital financial products receiving outsized scrutiny. Claims like "no hidden fees,"
                  "instant cash advance," and "build your credit" — the language of consumer fintech — are
                  exactly what examiners are trained to challenge.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  Clarid gives your compliance team the same unfair advantage your product team already has — technology that does the work at scale.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 3: Platform Capabilities ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
              What Clarid Does
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {capabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.1}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-[#8C58D0]/30 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-[#8C58D0]/10 flex items-center justify-center group-hover:bg-[#8C58D0]/20 group-hover:scale-110 transition-all duration-300">
                      <cap.icon className="h-6 w-6 text-[#8C58D0]" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{cap.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          <FadeUp delay={0.4}>
            <p className="mt-14 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid integrates into the tools your team already uses — Microsoft Copilot, Claude, ChatGPT — through
              MCP (Model Context Protocol). Compliance intelligence embedded in daily workflows, not another portal.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 4: Why This Matters for Varo ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 mb-8">
                Why This Matters for Varo
              </h2>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {varoSpecificBenefits.map((benefit, i) => (
              <ScaleIn key={benefit.title} delay={i * 0.08}>
                <div className="group p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:border-[#8C58D0]/30 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-[#8C58D0]/10 flex items-center justify-center group-hover:bg-[#8C58D0]/20 group-hover:scale-110 transition-all duration-300">
                      <benefit.icon className="h-6 w-6 text-[#8C58D0]" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{benefit.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{benefit.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 5: Cross-Module Interconnectivity ─── */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />

        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-[#B088E3] mb-5">
                The Differentiator
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white mb-8">
                Everything Connects
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-400 max-w-5xl mx-auto leading-relaxed">
                Most compliance tools are point solutions — marketing review in one system, regulatory
                monitoring in another, board reporting in a spreadsheet. Clarid connects every module
                so findings, trends, and documentation flow together automatically.
              </p>
            </div>
          </FadeUp>

          <div className="space-y-5 lg:space-y-6 mt-14">
            {interconnectivityFlows.map((flow, i) => (
              <ScaleIn key={flow.title} delay={i * 0.08}>
                <div className="p-8 lg:p-10 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500">
                  <div className="flex items-center gap-4 mb-3">
                    <ArrowsPointingInIcon className="h-6 w-6 text-[#B088E3] flex-shrink-0" />
                    <h3 className="text-xl font-display font-bold text-white">{flow.title}</h3>
                  </div>
                  <p className="text-lg text-slate-400 leading-relaxed pl-10">{flow.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 6: Built With the Examination Perspective ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
              Built With the Examination Perspective
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            <ScaleIn delay={0.1}>
              <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Partners from the FDIC and Federal Reserve
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  A former FDIC Supervisory Examiner and a former Federal Reserve Supervisory
                  Examiner — with decades of combined examination experience — are partners
                  in Clarid. Every rule engine, every workflow, every output is validated
                  against what examiners actually evaluate. The outputs speak their language
                  because the people who built them are the people who used to sit on the
                  other side of the table.
                </p>
              </div>
            </ScaleIn>

            <ScaleIn delay={0.2}>
              <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Built by the Buyer
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  Clarid's founder is a community bank CMO who manages marketing compliance
                  and examiner relationships as part of his daily work. This platform wasn't
                  designed by engineers guessing at compliance needs — it was built by someone
                  who writes the ads, reviews the disclosures, and sits across from examiners.
                  David will recognize the workflow because it mirrors how compliance actually works.
                </p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* ─── Section 7: Due Diligence ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                We've Done the Homework
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-14 leading-relaxed">
                Clarid maintains a comprehensive vendor due diligence package designed to meet
                interagency guidance on third-party relationships. Key documentation includes:
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="max-w-3xl mx-auto">
                <ul className="space-y-5">
                  {dueDiligenceDocs.map((doc, i) => (
                    <FadeIn key={doc} delay={0.15 + i * 0.05}>
                      <li className="flex items-start gap-4">
                        <CheckCircleIcon className="h-6 w-6 text-[#8C58D0] flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-slate-600">{doc}</span>
                      </li>
                    </FadeIn>
                  ))}
                </ul>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 8: Moving Forward ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
                Moving Forward
              </h2>
            </FadeUp>

            <div className="space-y-10 lg:space-y-12">
              {nextSteps.map((step, i) => (
                <FadeUp key={step.num} delay={i * 0.12}>
                  <div className="flex items-start gap-7">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-navy-900 text-white flex items-center justify-center text-lg font-bold">
                      {step.num}
                    </div>
                    <div className="pt-2">
                      <h3 className="text-xl md:text-2xl font-display font-bold text-navy-900 mb-2">{step.title}</h3>
                      <p className="text-lg text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 9: Contact ─── */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder & CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-[#8C58D0]">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-[#7040B0] transition-colors">
                  bobby@clarid.ai
                </a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Minimal Footer ─── */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/varo-logo.svg" alt="Varo Bank" className="h-6 opacity-40" />
            <span className="text-slate-200">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
          </div>
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Clarid. Confidential.
          </p>
        </div>
      </footer>
    </div>
  );
}
